import React, { type JSX, RefAttributes } from 'react'

import { LocaleButton, LocaleButtonIcon, LocaleButtonText } from '@syconium/little-miss-figgy'

import {
  PrimaryNavLocalizationDialogContent,
  PrimaryNavLocalizationDialogContentFooter,
  PrimaryNavLocalizationDialogHeader,
} from '../../../../app/_components/chrome/PrimaryNavLocalization.client'
import {
  NextDialog,
  NextDialogProvider,
  NextDialogTrigger,
} from '../../../../app/_components/dialogs/NextDialog.client'
import { useTranslatedLocalization } from '../../../../app/_hooks/useTranslatedLocalization'

import {
  CountryWrapper,
  FooterCopyright,
  FooterLeft,
  FooterLinksDivider,
  FooterLinksList,
  FooterLinksListItem,
  FooterListLink,
  FooterSocialLinkList,
  Promo,
  PromoHashtag,
  PromoSocialItem,
  PromoSocialLink,
  PromoSocialLinkSRText,
  PromoTagline,
  StyledFooter,
} from './styles'

export interface FooterSocialLinkProps {
  href: string
  text: string
  icon: React.ReactNode
}

const FooterSocialLink: React.FC<FooterSocialLinkProps> = ({ href, text, icon }) => {
  const pageName = text.length ? text.charAt(0).toUpperCase() + text.slice(1) : ''
  const ariaLabel = `Go to Wearfigs ${pageName} Page`

  return (
    <PromoSocialItem role='listitem'>
      <PromoSocialLink aria-label={ariaLabel} href={href}>
        {icon && (
          <span aria-hidden='true' role='presentation'>
            {icon}
          </span>
        )}
        <PromoSocialLinkSRText>{text}</PromoSocialLinkSRText>
      </PromoSocialLink>
    </PromoSocialItem>
  )
}

export interface FooterPromoProps {
  children?: React.ReactNode
  hashtag: string
  socialLinks: FooterSocialLinkProps[]
}

export const FooterPromo: React.FC<FooterPromoProps> = ({ children, hashtag, socialLinks }) => {
  return (
    <Promo>
      <PromoTagline>{children}</PromoTagline>
      <PromoHashtag>#{hashtag}</PromoHashtag>
      {socialLinks.length > 0 && (
        <FooterSocialLinkList role='list'>
          {socialLinks.map((link, index) => (
            <FooterSocialLink {...link} key={`${link.text}-${index}`} />
          ))}
        </FooterSocialLinkList>
      )}
    </Promo>
  )
}

export interface LinkItem {
  href: string
  title: string
}

export interface FooterGetHelpProps {
  helpCenter: LinkItem
  shipping: LinkItem
  returnsAndExchanges: LinkItem
  teamsOrders: LinkItem
  contactUs: LinkItem
  phone: {
    number: string
    cta: string
    timeText: string
    availableDaysText: string
  }
  chat: {
    title: string
    href: string
    timeText: string
    availableDaysText: string
  }
  children?: React.ReactNode
}

export interface FooterReferAFriendProps {
  href: string
  text: string
}

export interface FooterMenuProps {
  children?: React.ReactNode
  accordionIndex: number
  isAccordionDisabled?: boolean
}

export interface FooterMenuLinkProps {
  children: React.ReactNode
  href: string
}

export interface FooterFooterProps {
  asTag?: keyof JSX.IntrinsicElements
  copyright: string
  legalLinks?: LinkItem[]
  children?: React.ReactNode
}

export const FooterFooter: React.FC<FooterFooterProps> = ({
  asTag = 'footer',
  copyright,
  legalLinks,
}) => {
  const translatedLocalization = useTranslatedLocalization()
  const regionName = translatedLocalization.data.region.name
  const languageName = translatedLocalization.data.languageName
  const flagSrc = translatedLocalization.data.region.flagSrc
  const languageCount = translatedLocalization.data.region.languagesArray.length
  const languagePostfix = languageCount > 1 ? ` | ${languageName}` : ''

  return (
    <StyledFooter as={asTag}>
      <FooterLeft>
        <FooterCopyright>{copyright}</FooterCopyright>
        <FooterLinksList role='list' divider={<FooterLinksDivider>•</FooterLinksDivider>}>
          {legalLinks?.map(link => {
            return (
              <FooterLinksListItem key={link.title} role='listitem'>
                <FooterListLink href={link.href}> {link.title} </FooterListLink>
              </FooterLinksListItem>
            )
          })}
        </FooterLinksList>
      </FooterLeft>
      <NextDialogProvider>
        <CountryWrapper>
          <NextDialogTrigger render={<LocaleButton isColorInverted={true} />}>
            <LocaleButtonIcon alt='' src={flagSrc} />
            <LocaleButtonText>{`${regionName}${languagePostfix}`}</LocaleButtonText>
          </NextDialogTrigger>
        </CountryWrapper>
        <NextDialog
          analyticsTitle='Footer Localization'
          variant='default'
          unmountOnHide
          header={<PrimaryNavLocalizationDialogHeader />}
          footer={<PrimaryNavLocalizationDialogContentFooter />}
        >
          <PrimaryNavLocalizationDialogContent />
        </NextDialog>
      </NextDialogProvider>
    </StyledFooter>
  )
}

export default interface FooterProps extends RefAttributes<HTMLDivElement> {
  children?: React.ReactNode
  promoChildren(): React.ReactNode
  hashtag: string
  copyright: string
  termsLinkHref: string
  termsLinkText: string
  privacyLinkHref: string
  privacyLinkText: string
}
